import { type ReactNode } from 'react';

import cn from 'classnames';

import { useSceneCycle } from 'hooks/useSceneCycle';

import styles from './EnrichmentDeliveryPanels.module.scss';

type PanelProps = {
  active?: boolean;
};

type PanelFrameProps = {
  title: string;
  scene: number;
  scenes: ReactNode[];
};

const PanelFrame = ({ title, scene, scenes }: PanelFrameProps) => (
  <div className={styles.panel}>
    <div className={styles.panelHead}>
      <span className={styles.panelTitle}>{title}</span>

      {scenes.length > 1 && (
        <span className={styles.steps} aria-hidden="true">
          {scenes.map((_, i) => (
            <i key={i} className={cn(styles.step, i === scene && styles.on)} />
          ))}
        </span>
      )}
    </div>

    <div className={styles.sceneWrap}>
      <div key={scene} className={styles.scene}>
        {scenes[scene]}
      </div>
    </div>
  </div>
);

const GradeRows = ({ rows }: { rows: readonly (readonly [string, string, string])[] }) => (
  <div className={styles.grades}>
    {rows.map(([grade, label, count]) => (
      <div key={grade} className={styles.gradeRow}>
        <span className={cn(styles.gradeBadge, styles[`grade${grade}`])}>{grade}</span>
        <span className={styles.gradeLabel}>{label}</span>
        <span className={styles.gradeCount}>{count}</span>
      </div>
    ))}
  </div>
);

export const EnrichmentApiPanel = ({ active = true }: PanelProps) => {
  const scene = useSceneCycle(3, { active, intervalMs: 3200 });

  return (
    <PanelFrame
      title="API & MCP"
      scene={scene}
      scenes={[
        <div key="rest" className={styles.code}>
          <span className={styles.method}>GET</span>{' '}
          <span className={styles.path}>/v1/enrich?number=00048839&amp;country=GB</span>
          <br />
          <br />
          {'{'}
          <br />
          &nbsp;&nbsp;
          <span className={styles.key}>
            {'"'}legal_name{'"'}
          </span>
          :{' '}
          <span className={styles.str}>
            {'"'}BARCLAYS PLC{'"'}
          </span>
          ,
          <br />
          &nbsp;&nbsp;
          <span className={styles.key}>
            {'"'}status{'"'}
          </span>
          :{' '}
          <span className={styles.str}>
            {'"'}Active{'"'}
          </span>
          ,<br />
          &nbsp;&nbsp;
          <span className={styles.key}>
            {'"'}source{'"'}
          </span>
          : {'{'}
          <br />
          &nbsp;&nbsp;&nbsp;&nbsp;
          <span className={styles.key}>
            {'"'}registry{'"'}
          </span>
          :{' '}
          <span className={styles.str}>
            {'"'}Companies House{'"'}
          </span>
          <br />
          &nbsp;&nbsp;{'}'}
          <br />
          {'}'}
          <br />
          <br />
          <span className={styles.comment}>{'// provenance on every field, every response'}</span>
        </div>,

        <div key="mcp" className={styles.mcp}>
          <span className={styles.mcpEyebrow}>Free · any MCP client</span>
          <a className={styles.mcpUrl} href="https://www.globaldatabase.com/mcp">
            <span>
              globaldatabase.com<b>/mcp</b>
            </span>
            <span className={styles.mcpGo}>Install free →</span>
          </a>
          <p className={styles.mcpNote}>Point Claude, Cursor or any MCP client at one endpoint.</p>
        </div>,

        <div key="endpoints" className={styles.endpoints}>
          {[
            ['GET', '/v1/enrich'],
            ['GET', '/v1/company/:id'],
            ['POST', '/v1/batch'],
          ].map(([method, path]) => (
            <div key={path} className={styles.endpointRow}>
              <span className={styles.method}>{method}</span>
              <span className={styles.path}>{path}</span>
            </div>
          ))}
        </div>,
      ]}
    />
  );
};

const CSV_ROWS = [
  ['revolut.com', '08804411', '£1.8bn'],
  ['Barclays', '00048839', '£25.4bn'],
  ['Cushman & Wakefield', '—', '—'],
  ['acme-widgets.de', 'HRB 88221', '€42m'],
] as const;

const BULK_GRADES = [
  ['A', 'Exact match', '10,168'],
  ['B', 'Strong match', '1,363'],
  ['C', 'Needs review', '483'],
] as const;

export const EnrichmentBulkPanel = ({ active = true }: PanelProps) => {
  const scene = useSceneCycle(3, { active, intervalMs: 2800 });

  return (
    <PanelFrame
      title="Self-serve upload"
      scene={scene}
      scenes={[
        <div key="drop" className={styles.dropzone}>
          <span className={styles.dropIco}>XLSX</span>
          <span className={styles.dropMeta}>
            <b>companies.xlsx</b>
            <i>12,400 rows</i>
          </span>
          <span className={styles.dropHint}>Drop a .csv, .txt or .xlsx</span>
        </div>,

        <div key="csv" className={styles.csv}>
          <div className={cn(styles.csvRow, styles.csvHead)}>
            <div>your column</div>
            <div>company_no</div>
            <div>revenue</div>
          </div>

          {CSV_ROWS.map(([name, no, rev]) => (
            <div key={name} className={styles.csvRow}>
              <div>{name}</div>
              <div className={styles.csvNew}>{no}</div>
              <div className={styles.csvNew}>{rev}</div>
            </div>
          ))}
        </div>,

        <GradeRows key="grades" rows={BULK_GRADES} />,
      ]}
    />
  );
};

const FIELD_MAPPINGS = [
  ['company_number', 'gd_company_number'],
  ['legal_name', 'gd_legal_name'],
  ['revenue', 'gd_annual_revenue'],
  ['ubo', 'gd_beneficial_owners'],
  ['sanctions', 'gd_risk_flags'],
] as const;

const CRM_APPS = [
  ['Salesforce', 'Native'],
  ['HubSpot', 'Native'],
  ['Microsoft Dynamics', 'Native'],
] as const;

export const EnrichmentCrmPanel = ({ active = true }: PanelProps) => {
  const scene = useSceneCycle(3, { active, intervalMs: 2800 });

  return (
    <PanelFrame
      title="CRM sync"
      scene={scene}
      scenes={[
        <div key="mapping" className={styles.mappings}>
          {FIELD_MAPPINGS.map(([src, dst]) => (
            <div key={src} className={styles.mapRow}>
              <span>{src}</span>
              <span className={styles.mapArrow}>→</span>
              <span className={styles.mapDst}>{dst}</span>
            </div>
          ))}
        </div>,

        <div key="apps" className={styles.apps}>
          {CRM_APPS.map(([name, tag]) => (
            <div key={name} className={styles.appRow}>
              <span className={styles.appName}>{name}</span>
              <span className={styles.appTag}>{tag}</span>
              <span className={styles.appBtn}>Free install</span>
            </div>
          ))}
        </div>,

        <div key="sync" className={styles.syncCard}>
          <span className={styles.syncDot} />
          <span className={styles.syncBig}>Two-way sync</span>
          <span className={styles.syncNote}>1,204 accounts refreshed · last run 2 min ago</span>
        </div>,
      ]}
    />
  );
};

const CHANNELS = ['Amazon S3', 'SFTP', 'Flat files'] as const;
const CADENCES = ['Daily', 'Weekly', 'Quarterly'] as const;

export const EnrichmentLicensingPanel = ({ active = true }: PanelProps) => {
  const scene = useSceneCycle(3, { active, intervalMs: 2600 });

  return (
    <PanelFrame
      title="Bulk delivery"
      scene={scene}
      scenes={[
        <div key="stat" className={styles.licensingStat}>
          <span className={styles.licensingNum}>600M+</span>
          <span className={styles.licensingLabel}>company records across 200+ countries</span>
        </div>,

        <div key="channels" className={styles.channels}>
          {CHANNELS.map((channel) => (
            <span key={channel} className={styles.channel}>
              {channel}
            </span>
          ))}
        </div>,

        <div key="cadence" className={styles.cadence}>
          <span className={styles.cadenceBig}>Synced daily</span>
          <span className={styles.cadencePills}>
            {CADENCES.map((cadence, i) => (
              <span key={cadence} className={cn(styles.cadenceItem, i === 0 && styles.on)}>
                {cadence}
              </span>
            ))}
          </span>
        </div>,
      ]}
    />
  );
};

const CONNECTORS = ['Claude', 'ChatGPT', 'Cursor'] as const;

export const EnrichmentAiPanel = ({ active = true }: PanelProps) => {
  const scene = useSceneCycle(3, { active, intervalMs: 3000 });

  return (
    <PanelFrame
      title="AI connectors"
      scene={scene}
      scenes={[
        <div key="chat" className={styles.chat}>
          <div className={cn(styles.bubble, styles.bubbleUser)}>
            Enrich these 240 accounts with registration numbers and UBOs.
          </div>

          <div className={cn(styles.bubble, styles.bubbleAi)}>
            Done — 236 of 240 matched. Each field cites its registry filing.
          </div>
        </div>,

        <div key="installs" className={styles.installs}>
          {CONNECTORS.map((name) => (
            <div key={name} className={styles.installRow}>
              <span className={styles.installDot} />
              <span className={styles.installName}>{name}</span>
              <span className={styles.installCta}>Install</span>
            </div>
          ))}
        </div>,

        <div key="citation" className={styles.citation}>
          <span className={styles.citationField}>legal_name</span>
          <span className={styles.citationValue}>BARCLAYS PLC</span>
          <span className={styles.citationSrc}>Sourced from Companies House filing #00048839</span>
        </div>,
      ]}
    />
  );
};

const MANAGED_GRADES = [
  ['A', 'Exact match', '9,842'],
  ['B', 'Strong match', '1,908'],
  ['C', 'Needs review', '250'],
] as const;

export const EnrichmentManagedServicePanel = ({ active = true }: PanelProps) => {
  const scene = useSceneCycle(3, { active, intervalMs: 2800 });

  return (
    <PanelFrame
      title="Managed service"
      scene={scene}
      scenes={[
        <div key="ticket" className={styles.dropzone}>
          <span className={styles.dropIco}>XLSX</span>
          <span className={styles.dropMeta}>
            <b>companies.xlsx</b>
            <i>12,400 rows</i>
          </span>
          <span className={styles.dropHint}>Send it — any size, any format</span>
        </div>,

        <div key="sla" className={styles.slaCard}>
          <span className={styles.slaBig}>&lt; 1 business day</span>
          <span className={styles.slaNote}>Quote and turnaround sent to your inbox</span>
        </div>,

        <GradeRows key="grades" rows={MANAGED_GRADES} />,
      ]}
    />
  );
};
